import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { MainLayout } from '../layouts';
import { Button } from '../components/ui/Button';
import { SUBMIT_CONTACT_FORM, ContactFormInput, ContactFormResponse } from '@shared/graphql/mutations/contact-form';
import analyticsService from '../services/analytics';
import { usePageDocumentTitle } from '../hooks/useDocumentTitle';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const ContactPage: React.FC = () => {
  usePageDocumentTitle('Contact Us');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState<string>('');
  
  const [submitContactForm, { loading: isSubmitting }] = useMutation<
    { submitContactForm: ContactFormResponse },
    { input: ContactFormInput }
  >(SUBMIT_CONTACT_FORM);

  // Track page view when component mounts
  useEffect(() => {
    try {
      analyticsService.trackPageView('contact', undefined, new Date().toISOString().split('T')[0], {
        referrer: document.referrer || 'direct'
      });
      console.log('📊 ContactPage: Page view tracked');
    } catch (analyticsError) {
      console.warn('Analytics tracking failed:', analyticsError);
    }
  }, []);
  
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await submitContactForm({
        variables: {
          input: {
            name: formData.name.trim(),
            email: formData.email.trim(),
            subject: formData.subject.trim(),
            message: formData.message.trim()
          }
        }
      });
      
      if (result.data?.submitContactForm.success) {
        // Track successful form submission
        try {
          analyticsService.trackEvent('contact_form_submitted', {
            pageType: 'contact',
            selectedDate: new Date().toISOString().split('T')[0],
            metadata: {
              subject: formData.subject,
              success: true
            }
          });
          console.log('📊 ContactPage: Form submission tracked');
        } catch (analyticsError) {
          console.warn('Analytics tracking failed for form submission:', analyticsError);
        }

        setSubmitStatus('success');
        setSubmitMessage(result.data.submitContactForm.message);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.data?.submitContactForm.message || 'An error occurred while sending your message.');
      }
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
      
      // Handle GraphQL errors
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        setSubmitMessage(error.graphQLErrors[0].message);
      } else if (error.networkError) {
        setSubmitMessage('Network error. Please check your connection and try again.');
      } else {
        setSubmitMessage('An unexpected error occurred. Please try again later.');
      }
    }
  };
  
  return (
    <MainLayout>
      <div className="bg-white p-6 rounded-lg shadow-card">
        <h1 className="text-2xl font-bold mb-6">Contact Us</h1>
        
        <p className="mb-6">
          Have a question or feedback? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
        </p>
        
        {submitStatus === 'success' ? (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            <p className="font-bold">Thank you for your message!</p>
            <p>{submitMessage}</p>
          </div>
        ) : submitStatus === 'error' ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <p className="font-bold">Something went wrong!</p>
            <p>{submitMessage}</p>
          </div>
        ) : null}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Your name"
              disabled={isSubmitting}
            />
            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Your email address"
              disabled={isSubmitting}
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>
          
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.subject ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Subject of your message"
              disabled={isSubmitting}
            />
            {errors.subject && <p className="mt-1 text-sm text-red-500">{errors.subject}</p>}
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={5}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Your message"
              disabled={isSubmitting}
            ></textarea>
            {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
          </div>
          
          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              fullWidth
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </form>
        
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h2 className="text-xl font-bold mb-3">Other Ways to Reach Us</h2>
          <p className="mb-2">
            <strong>Email:</strong> <a href="mailto:contact@tippingiq.com" className="text-blue-600 hover:underline">contact@tippingiq.com</a>
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ContactPage;
